import { supabase } from "@/integrations/supabase/client";
import {
  affiliateLinks as SEED,
  type AffiliateLinkConfig,
  type AffiliatePriority,
} from "@/config/affiliateLinks";

/**
 * Runtime-registry for affiliate-annoncer. Startes med hardkodet seed (så SSR
 * og første render har data), og overskrives af databasen når hydrate kaldes.
 * Admin-UI opdaterer databasen; alt UI læser fra registry via getAffiliateLink.
 */

type Registry = Map<string, AffiliateLinkConfig>;

function buildFromSeed(): Registry {
  const map: Registry = new Map();
  for (const [key, link] of Object.entries(SEED) as [string, AffiliateLinkConfig][]) {
    map.set(link.id, link);
    // Also index by the camelCase seed key so callers using AffiliateId keys
    // (e.g. "leasyMinilaan") resolve to the same config as the kebab-case id.
    if (key !== link.id) map.set(key, link);
  }
  return map;
}

/** Map camelCase seed key -> canonical kebab-case id (for DB-hydrated lookups). */
const SEED_KEY_TO_ID: Record<string, string> = Object.fromEntries(
  (Object.entries(SEED) as [string, AffiliateLinkConfig][]).map(([k, v]) => [k, v.id]),
);

let registry: Registry = buildFromSeed();
let hydrated = false;
let hydrating: Promise<void> | null = null;

const listeners = new Set<() => void>();
function emit() {
  for (const l of listeners) l();
}

export function subscribeAffiliateRegistry(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function withCampaign(link: AffiliateLinkConfig): AffiliateLinkConfig {
  // Kampagner findes kun i seed-config. Slå op og merge overrides ind hvis
  // en tidsbegrænset kampagne er aktiv, så DB-hydrerede links også får dem.
  const seed = (SEED as Record<string, AffiliateLinkConfig>)[link.id]
    ?? Object.values(SEED as Record<string, AffiliateLinkConfig>).find((l) => l.id === link.id);
  const c = seed?.campaign;
  if (!c) return link;
  const end = new Date(`${c.endsAt}T23:59:59.999`);
  if (Number.isNaN(end.getTime()) || Date.now() > end.getTime()) return link;
  return {
    ...link,
    advertiser: c.advertiser ?? link.advertiser,
    category: c.category ?? link.category,
    url: c.url ?? link.url,
    defaultCta: c.defaultCta ?? link.defaultCta,
    allowedContexts: c.allowedContexts ?? link.allowedContexts,
    priority: c.priority ?? link.priority,
  };
}

export function getAllAffiliateLinks(): AffiliateLinkConfig[] {
  return Array.from(registry.values()).map(withCampaign);
}

export function getAffiliateLink(idOrKey: string): AffiliateLinkConfig | undefined {
  const direct = registry.get(idOrKey);
  if (direct) return withCampaign(direct);
  const mapped = SEED_KEY_TO_ID[idOrKey];
  if (mapped) {
    const link = registry.get(mapped);
    if (link) return withCampaign(link);
  }
  return undefined;
}


export function isAffiliateRegistryHydrated(): boolean {
  return hydrated;
}

interface DbRow {
  id: string;
  network: string;
  advertiser: string;
  category: string;
  url: string;
  default_cta: string;
  allowed_contexts: string[] | null;
  priority: string;
  is_active: boolean;
  allowed_brand_ids: string[] | null;
  allowed_model_ids: string[] | null;
  languages: string[] | null;
  devices: string[] | null;
}

function rowToConfig(row: DbRow): AffiliateLinkConfig {
  const priority: AffiliatePriority =
    row.priority === "high" || row.priority === "low" ? row.priority : "medium";
  const devices = (row.devices ?? ["desktop", "mobile"]).filter(
    (d): d is "desktop" | "mobile" => d === "desktop" || d === "mobile",
  );
  return {
    id: row.id,
    network: row.network,
    advertiser: row.advertiser,
    category: row.category,
    url: row.url,
    defaultCta: row.default_cta,
    allowedContexts: row.allowed_contexts ?? [],
    priority,
    allowedBrandIds: row.allowed_brand_ids ?? [],
    allowedModelIds: row.allowed_model_ids ?? [],
    languages: row.languages ?? ["da"],
    devices: devices.length ? devices : ["desktop", "mobile"],
  };
}

export async function hydrateAffiliateRegistry(opts?: { includeInactive?: boolean }): Promise<void> {
  if (hydrating) return hydrating;
  hydrating = (async () => {
    try {
      let query = supabase
        .from("affiliate_ads" as any)
        .select("id,network,advertiser,category,url,default_cta,allowed_contexts,priority,is_active,sort_order,allowed_brand_ids,allowed_model_ids,languages,devices")
        .order("sort_order", { ascending: true });
      if (!opts?.includeInactive) query = query.eq("is_active", true);
      const { data, error } = await query;
      if (error) throw error;
      if (Array.isArray(data) && data.length) {
        const next: Registry = new Map();
        for (const row of data as unknown as DbRow[]) {
          next.set(row.id, rowToConfig(row));
        }
        registry = next;
        hydrated = true;
        emit();
      }
    } catch (err) {
      // Behold seed hvis DB fejler — så knækker siden ikke.
      console.warn("[affiliate-registry] hydrate failed, using seed", err);
    } finally {
      hydrating = null;
    }
  })();
  return hydrating;
}

/** Kaldes af admin efter en mutation for at genindlæse. */
export async function refetchAffiliateRegistry(): Promise<void> {
  hydrating = null;
  hydrated = false;
  await hydrateAffiliateRegistry();
}
